import type { CashAccountChoice, CreateExpenseInput, Expense, ExpenseHead } from '../api/cash';
import { paymentMethodLabel } from '../bookings/paymentWords';
import { formatAmount } from '../format/money';
import { PaymentMethodValues, type PaymentMethod } from '../generated/enums';
import { defaultPaidFrom, paidFromProblem } from './paidFrom';
import { filledParts, methodsFor, splitProblem, type SplitPart } from './split';

/**
 * Recording money going out — the rules of the website's New expense page and the phone's, kept
 * once (spec Section 18, `docs/Cash_Ledger_Design.md`).
 *
 * <p>
 * <strong>Every refusal here is the server's, mirrored</strong>, so a clerk is told while the bill
 * is still in their hand rather than by a 400: an amount above zero, a day, not in the future, and
 * a head that still takes vouchers. <strong>`Adjustment` is never offered</strong> — it means no
 * money changed hands, which is the one thing an expense is not, and the server refuses it.
 * </p>
 */

/** How an expense may be paid: every method but `Adjustment`. */
export const expenseMethods: readonly PaymentMethod[] = PaymentMethodValues.filter(
  (method) => method !== 'Adjustment',
);

/** The heads a voucher may be filed under. A retired head is refused by the server. */
export function payableHeads(heads: readonly ExpenseHead[] | undefined): ExpenseHead[] {
  return (heads ?? []).filter((head) => head.isActive);
}

/**
 * A code for a head added from inside a voucher or recurring form, where only its name is asked —
 * "Licences & fees" → `LICENCESFEES`, then `LICENCESFEES2` if that is taken. The Heads card still
 * asks for a code; this only saves a clerk mid-entry from leaving the form (2026-10-23).
 */
export function headCodeFor(name: string, heads: readonly Pick<ExpenseHead, 'code'>[]): string {
  const base =
    name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 28) || 'HEAD';
  const taken = new Set(heads.map((head) => head.code.toUpperCase()));

  if (!taken.has(base)) {
    return base;
  }

  for (let n = 2; ; n += 1) {
    if (!taken.has(`${base}${n}`)) {
      return `${base}${n}`;
    }
  }
}

/** A typed amount as a number, or null when it is not one above zero. */
export function expenseAmount(typed: string): number | null {
  const trimmed = typed.trim();

  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return null;
  }

  const value = Number(trimmed);

  return value > 0 ? value : null;
}

export interface ExpenseDraft {
  headId: number | null;
  amount: string;
  paidOn: string;
  method: PaymentMethod;
  /** The bill number, or a note — one box on the phone, sent as the reference. */
  reference: string;
  /** Paid from; null is the branch's default for the method. See `paidFrom.ts`. */
  cashAccountId: number | null;
  /**
   * Recorded from a recurring expense (step 8): which one and the day it fell due — the server
   * settles that occurrence with this voucher. Null for an ordinary expense. See `recurring.ts`.
   */
  recurrence: { recurringExpenseId: number; dueOn: string } | null;
}

export function emptyExpense(today: string): ExpenseDraft {
  return {
    headId: null,
    amount: '',
    paidOn: today,
    method: 'Cash',
    reference: '',
    cashAccountId: null,
    recurrence: null,
  };
}

/**
 * The one thing standing between the form and Save, or null — one at a time, most-blocking first,
 * the count's rule.
 */
export function expenseProblem(
  draft: ExpenseDraft,
  heads: readonly ExpenseHead[],
  today: string,
  accounts?: readonly CashAccountChoice[],
): string | null {
  if (heads.length === 0) {
    return 'This shop has no expense heads yet. Add one on the website, under Expenses.';
  }

  if (expenseAmount(draft.amount) === null) {
    return 'Type how much was paid.';
  }

  if (draft.headId === null) {
    return 'Choose what it was for.';
  }

  const head = heads.find((option) => option.id === draft.headId);

  if (!head || !head.isActive) {
    return 'That head no longer takes vouchers. Choose another.';
  }

  if (draft.method === 'Adjustment') {
    return 'An expense is money that moved. Choose how it was paid.';
  }

  if (draft.paidOn === '') {
    return 'Say which day it was paid.';
  }

  if (draft.paidOn > today) {
    return 'A day that has not happened yet cannot be paid on.';
  }

  return paidFromProblem(draft.cashAccountId, draft.method, accounts);
}

/** The request, or null while there is a problem — so nothing half-checked is ever sent. */
export function expenseRequest(
  draft: ExpenseDraft,
  heads: readonly ExpenseHead[],
  today: string,
  accounts?: readonly CashAccountChoice[],
): CreateExpenseInput | null {
  const amount = expenseAmount(draft.amount);

  if (
    expenseProblem(draft, heads, today, accounts) !== null ||
    amount === null ||
    draft.headId === null
  ) {
    return null;
  }

  return {
    expenseHeadId: draft.headId,
    amount,
    paidOn: draft.paidOn,
    method: draft.method,
    reference: draft.reference.trim() || null,
    notes: null,
    cashAccountId: draft.cashAccountId,
    recurringExpenseId: draft.recurrence?.recurringExpenseId ?? null,
    recurrenceDueOn: draft.recurrence?.dueOn ?? null,
  };
}

/* ------------------------------------------------------------- split across accounts */

/**
 * The boxes a draft starts with: its amount (a recurring expense's typical figure) in the box of the
 * account it names, or the method's default — so a voucher opened from a due expense reads as before.
 * That box moves to the top with the draft's method, typed or not: an "Again, like last time" tile
 * names the account and method it was last paid by, and the box on top is the one it means.
 */
export function expenseParts(
  parts: readonly SplitPart[],
  draft: Pick<ExpenseDraft, 'amount' | 'method' | 'cashAccountId'>,
  accounts: readonly CashAccountChoice[] | undefined,
): SplitPart[] {
  const typed = draft.amount.trim() !== '';
  const target = draft.cashAccountId ?? defaultPaidFrom(draft.method, accounts);

  // A plain new voucher names nothing and keeps the boxes in their usual order.
  if (target === null || (!typed && draft.cashAccountId === null)) {
    return [...parts];
  }

  const named = parts.find((part) => part.accountId === target);

  if (!named) {
    return [...parts];
  }

  // The account it names comes first with its method set — "Again, like last time" brings the
  // account and method but never the amount, so the empty box on top is where to type.
  const lead: SplitPart = {
    ...named,
    amount: typed ? draft.amount : named.amount,
    method: methodsFor(named.kind).includes(draft.method) ? draft.method : named.method,
  };

  return [lead, ...parts.filter((part) => part.accountId !== target)];
}

/**
 * What stops a split expense being saved (owner, 2026-11-01: **several vouchers**, one per
 * account): the voucher's own rules — a live head, a day that has come — and each box a figure. A
 * recurring expense settles one occurrence with one voucher, so it is paid from one account.
 */
export function expensePartsProblem(
  draft: ExpenseDraft,
  heads: readonly ExpenseHead[],
  today: string,
  parts: readonly SplitPart[],
): string | null {
  if (heads.length === 0) {
    return 'This shop has no expense heads yet. Add one on the website, under Expenses.';
  }

  const boxes = splitProblem(parts, 'from');

  if (boxes) {
    return boxes;
  }

  const bad = filledParts(parts).find((part) => expenseAmount(part.amount) === null);

  if (bad) {
    return `The amount for ${bad.accountName} must be a figure above zero, to the paisa at most.`;
  }

  if (draft.recurrence && filledParts(parts).length > 1) {
    return 'A recurring expense is one voucher, so it is paid from one account.';
  }

  if (draft.headId === null) {
    return 'Choose what it was for.';
  }

  const head = heads.find((option) => option.id === draft.headId);

  if (!head || !head.isActive) {
    return 'That head no longer takes vouchers. Choose another.';
  }

  if (draft.paidOn === '') {
    return 'Say which day it was paid.';
  }

  return draft.paidOn > today ? 'A day that has not happened yet cannot be paid on.' : null;
}

/**
 * One voucher per box with money in it, each naming its account, the bill number on every one —
 * or null while there is a problem. Sent one by one (`payInParts`).
 */
export function expenseRequests(
  draft: ExpenseDraft,
  heads: readonly ExpenseHead[],
  today: string,
  parts: readonly SplitPart[],
): CreateExpenseInput[] | null {
  if (expensePartsProblem(draft, heads, today, parts) !== null || draft.headId === null) {
    return null;
  }

  const headId = draft.headId;

  return filledParts(parts).map((part) => ({
    expenseHeadId: headId,
    amount: expenseAmount(part.amount) ?? 0,
    paidOn: draft.paidOn,
    method: part.method,
    reference:
      [draft.reference.trim(), part.reference.trim()].filter((text) => text !== '').join(' · ') ||
      null,
    notes: null,
    cashAccountId: part.accountId,
    recurringExpenseId: draft.recurrence?.recurringExpenseId ?? null,
    recurrenceDueOn: draft.recurrence?.dueOn ?? null,
  }));
}

/** "Save ₹180", "Save ₹1,200 · 2 vouchers" — a voucher per account, so the count says how many. */
export function saveExpensesLabel(total: number, vouchers: number, currency = ''): string {
  if (vouchers === 0) {
    return 'Type the amount';
  }

  const many = vouchers > 1 ? ` · ${vouchers} vouchers` : '';

  return `Save ${currency}${formatAmount(total)}${many}`;
}

/**
 * The save button, naming the money and where it came from: "Save ₹180 from cash", or, with the
 * account chosen, "Save ₹180 from Petty cash".
 */
export function saveExpenseLabel(
  amount: number | null,
  method: PaymentMethod,
  currency = '',
  from?: string,
): string {
  if (amount === null) {
    return 'Type the amount';
  }

  const figure = `${currency}${formatAmount(amount)}`;

  if (method === 'Cash') {
    return `Save ${figure} from ${from ?? 'cash'}`;
  }

  return `Save ${figure} paid by ${paymentMethodLabel[method]}${from ? ` from ${from}` : ''}`;
}

/** One "Again, like last time" tile on Money out: a head as it was last paid. */
export interface LastPaid {
  headId: number;
  headName: string;
  method: PaymentMethod;
  cashAccountId: number | null;
  cashAccountName: string | null;
  lastAmount: number;
}

/**
 * The heads most recently paid, newest first, one tile each, as they were last paid — read from the
 * register's first page, so nothing new is stored (canvas "Cash & expense flow", board 3). Voided
 * vouchers and retired heads are left out: a tile that opens a refusal is worse than none.
 */
export function againLikeLastTime(
  recent: readonly Pick<
    Expense,
    | 'expenseHeadId'
    | 'headName'
    | 'method'
    | 'cashAccountId'
    | 'cashAccountName'
    | 'amount'
    | 'isVoided'
  >[],
  heads: readonly ExpenseHead[] | undefined,
  limit = 4,
): LastPaid[] {
  const open = new Set(payableHeads(heads).map((head) => head.id));
  const seen = new Set<number>();
  const tiles: LastPaid[] = [];

  for (const expense of recent) {
    if (tiles.length >= limit) {
      break;
    }

    if (expense.isVoided || seen.has(expense.expenseHeadId) || !open.has(expense.expenseHeadId)) {
      continue;
    }

    seen.add(expense.expenseHeadId);
    tiles.push({
      headId: expense.expenseHeadId,
      headName: expense.headName,
      method: expense.method,
      cashAccountId: expense.cashAccountId,
      cashAccountName: expense.cashAccountName,
      lastAmount: expense.amount,
    });
  }

  return tiles;
}

/**
 * The voucher a tile opens: the head, method and account filled in, paid today. **The amount is
 * typed fresh** — last time's tea is not this time's, and a figure already in the box is one a
 * hurried clerk saves unread.
 */
export function expenseDraftLike(last: LastPaid, today: string): ExpenseDraft {
  return {
    ...emptyExpense(today),
    headId: last.headId,
    method: last.method,
    cashAccountId: last.cashAccountId,
  };
}

/** "Cash · Petty cash · last ₹180" — the tile's second line. */
export function lastPaidLine(last: LastPaid, currency = ''): string {
  return [
    paymentMethodLabel[last.method],
    last.cashAccountName,
    `last ${currency}${formatAmount(last.lastAmount)}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The More tab's line under Expenses, a row drawn only for a role that may add one: today's
 * vouchers when the role may read them (null otherwise), then the invitation.
 */
export function expensesTodayLine(countToday: number | null): string {
  if (countToday === null) {
    return 'Tap to add one';
  }

  return `${countToday === 0 ? 'None' : countToday} today · tap to add one`;
}
